import type { ControlledActionBlockType, ResolvedControlActionWithPayload } from '@lighthouse/core'
import { keys } from 'rambda'
import { useEffect } from 'react'

import { getWithScopeId } from '../block'

type ExtractPayload<CAP, CAT> = CAP extends { blockType: CAT; payload: infer P } ? P : never

type ExtractParams<CP, ActionType> = CP extends { type: ActionType; params: infer Params } ? Params : never

/**
 * 事件系统
 */
export class BlockListenerSystem {
    private listeners: Record<string, Record<string, Set<(payload?: any) => void>>> = {}

    register<
        BlockType extends ControlledActionBlockType,
        CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>,
        E extends CP['type'],
        Params extends ExtractParams<CP, E>
    >(
        blockId: string,
        blockType: BlockType,
        listenerMap: {
            [K in E]?: (payload?: Params) => void
        },
        scope?: string
    ) {
        const listenerKey = getWithScopeId(blockId, scope)
        const listenerEventKeys = keys(listenerMap)
        for (const listenerEventKey of listenerEventKeys) {
            if (!listenerMap[listenerEventKey]) {
                throw new Error(`Event listener for ${listenerEventKey} is not provided`)
            }

            if (typeof listenerMap[listenerEventKey] !== 'function') {
                throw new TypeError(`Event listener for ${listenerEventKey} is not a function`)
            }

            if (!this.listeners[listenerKey]) {
                this.listeners[listenerKey] = {}
            }
            if (!this.listeners[listenerKey][listenerEventKey]) {
                this.listeners[listenerKey][listenerEventKey] = new Set()
            }
            if (listenerMap[listenerEventKey]) {
                this.listeners[listenerKey][listenerEventKey].add(listenerMap[listenerEventKey] as (payload?: any) => void)
            }
        }

        return (event?: CP['type']) => {
            const eventKeys = event ? [event] : (listenerEventKeys as CP['type'][])
            eventKeys.forEach(eventKey => {
                const listener = listenerMap[eventKey as E]
                if (!listener) {
                    return
                }
                this.listeners[listenerKey]?.[eventKey]?.delete(listener as (payload?: any) => void)
                if (this.listeners[listenerKey]?.[eventKey]?.size === 0) {
                    delete this.listeners[listenerKey][eventKey]
                }
            })
            if (this.listeners[listenerKey] && Object.keys(this.listeners[listenerKey]).length === 0) {
                delete this.listeners[listenerKey]
            }
        }
    }

    unregister<BlockType extends ControlledActionBlockType, CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>>(
        blockId: string,
        blockType: BlockType,
        events?: CP['type'][],
        scope?: string
    ) {
        const id = getWithScopeId(blockId, scope)
        if (events) {
            events.forEach(event => {
                delete this.listeners[id]?.[event]
            })
            if (this.listeners[id] && Object.keys(this.listeners[id]).length === 0) {
                delete this.listeners[id]
            }
            return
        }
        return delete this.listeners?.[id]
    }

    emit<
        BlockType extends ControlledActionBlockType,
        CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>,
        E extends CP['type'],
        Params extends ExtractParams<CP, E>
    >(blockId: string, blockType: BlockType, { event, params }: { event: E; params?: Params }, scope?: string) {
        const id = getWithScopeId(blockId, scope)
        const listeners = this.listeners[id]?.[event]

        if (listeners) {
            listeners.forEach(listener => listener(params))
        }
    }
}

export const blockListenerSystem = new BlockListenerSystem()

export function useRegisterBlockListener<
    BlockType extends ControlledActionBlockType,
    CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>,
    E extends CP['type']
>(
    id: string | undefined,
    blockType: BlockType,
    listenerMap: {
        [K in E]?: (payload?: ExtractParams<CP, K>) => void
    }
) {
    useEffect(() => {
        if (!id) {
            return
        }
        // @ts-expect-error listenerMap is expected to be a valid listener map
        const unregister = blockListenerSystem.register(id, blockType, listenerMap)

        return () => {
            unregister()
        }
    }, [id, blockType, listenerMap])
}

export function useRegisterBlockListeners<
    BlockType extends ControlledActionBlockType,
    CP extends ExtractPayload<ResolvedControlActionWithPayload, BlockType>,
    E extends CP['type']
>(
    ids: readonly string[],
    blockType: BlockType,
    listenerMap: {
        [K in E]?: (payload?: ExtractParams<CP, K>) => void
    }
) {
    const idKey = ids.join('\0')
    useEffect(() => {
        if (!idKey) {
            return
        }
        const removeListeners = idKey.split('\0').map(listenerId =>
            // @ts-expect-error listenerMap is expected to be a valid listener map
            blockListenerSystem.register(listenerId, blockType, listenerMap)
        )
        return () => {
            removeListeners.forEach(removeListener => removeListener())
        }
    }, [blockType, idKey, listenerMap])
}
